// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import './SaveLoadModal.css';
import saveLoadManager from '../utils/system/SaveLoadManager';
import apiSaveLoadManager from '../utils/system/ApiSaveLoadManager';

function SaveLoadModal({ isOpen, onClose, onSaveComplete, onLoadComplete }) {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('info');
  const [showRenameInput, setShowRenameInput] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [showSaveNameInput, setShowSaveNameInput] = useState(false);
  const [saveNameValue, setSaveNameValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadSlots();
      setMessage(null);
      setShowRenameInput(null);
      setShowSaveNameInput(false);
    }
  }, [isOpen]);

  const loadSlots = () => {
    const allSlots = saveLoadManager.getAllSaveSlots();
    setSlots(allSlots);
    
    // Select current slot by default
    const currentSlot = saveLoadManager.getCurrentSlot();
    setSelectedSlot(currentSlot);
  };

  const handleSlotSelect = (slotNumber) => {
    setSelectedSlot(slotNumber);
    setShowRenameInput(null);
    setShowSaveNameInput(false);
  };

  const handleSave = async () => {
    if (!selectedSlot) {
      setMessage('Please select a slot');
      setMessageType('error');
      return;
    }

    // Check if slot is empty or ask for name
    const slot = slots.find(s => s.slot === selectedSlot);
    if (slot && slot.isEmpty && !showSaveNameInput) {
      setShowSaveNameInput(true);
      setSaveNameValue(`Save ${selectedSlot}`);
      return;
    }

    const saveName = showSaveNameInput ? saveNameValue : slot?.slotName;
    
    // Get auth token
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      // Save to API if authenticated
      const apiResult = await apiSaveLoadManager.saveToApi(token, saveName);
      if (!apiResult) {
        setMessage(`API save failed`);
        setMessageType('error');
        return;
      }
    }
    
    // Also save locally for backwards compatibility
    const result = saveLoadManager.save(selectedSlot, saveName);
    
    if (result.success) {
      setMessage(`Saved to Slot ${selectedSlot}: ${result.slotName}`);
      setMessageType('success');
      loadSlots();
      setShowSaveNameInput(false);
      
      if (onSaveComplete) {
        onSaveComplete();
      }
      
      setTimeout(() => {
        onClose();
      }, 1500);
    } else {
      setMessage(`Save failed: ${result.error}`);
      setMessageType('error');
    }
  };

  const handleLoad = () => {
    if (!selectedSlot) {
      setMessage('Please select a slot');
      setMessageType('error');
      return;
    }

    const slot = slots.find(s => s.slot === selectedSlot);
    if (slot && slot.isEmpty) {
      setMessage('This slot is empty');
      setMessageType('error');
      return;
    }

    const result = saveLoadManager.load(selectedSlot);
    if (result.success) {
      setMessage('Game loaded! Refreshing...');
      setMessageType('success');
      
      if (onLoadComplete) {
        onLoadComplete();
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      setMessage(`Load failed: ${result.error}`);
      setMessageType('error');
    }
  };

  const handleDelete = () => {
    if (!selectedSlot) {
      setMessage('Please select a slot');
      setMessageType('error');
      return;
    }

    const slot = slots.find(s => s.slot === selectedSlot);
    if (slot && slot.isEmpty) {
      setMessage('This slot is already empty');
      setMessageType('info');
      return;
    }

    if (window.confirm(`Delete save in Slot ${selectedSlot}? This cannot be undone!`)) {
      const result = saveLoadManager.deleteSave(selectedSlot);
      if (result.success) {
        setMessage(`Slot ${selectedSlot} deleted`);
        setMessageType('info');
        loadSlots();
      } else {
        setMessage(`Delete failed: ${result.error}`);
        setMessageType('error');
      }
    }
  };

  const handleRename = (slotNumber) => {
    const slot = slots.find(s => s.slot === slotNumber);
    if (slot && !slot.isEmpty) {
      setShowRenameInput(slotNumber);
      setRenameValue(slot.slotName || `Save ${slotNumber}`);
    }
  };

  const handleRenameSubmit = (slotNumber) => {
    if (!renameValue.trim()) {
      setMessage('Name cannot be empty');
      setMessageType('error');
      return;
    }

    const result = saveLoadManager.renameSave(slotNumber, renameValue.trim());
    if (result.success) {
      setMessage(`Renamed to: ${renameValue}`);
      setMessageType('success');
      loadSlots();
      setShowRenameInput(null);
      
      setTimeout(() => setMessage(null), 2000);
    } else {
      setMessage(`Rename failed: ${result.error}`);
      setMessageType('error');
    }
  };

  const handleSaveNameSubmit = () => {
    if (!saveNameValue.trim()) {
      setMessage('Name cannot be empty');
      setMessageType('error');
      return;
    }
    handleSave();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="💾 SAVE / LOAD GAME"
      size="large"
      closeOnBackdrop={true}
      closeOnEscape={true}
      showCloseButton={true}
      className="save-load-modal-custom"
    >
      {/* Message Display */}
      {message && (
        <div className={`save-load-message ${messageType}`}>
          {message}
        </div>
      )}

      {/* Save Name Input (for new saves) */}
          {showSaveNameInput && (
            <div className="save-name-input-section">
              <h3>Name Your Save</h3>
              <div className="save-name-input-group">
                <input
                  type="text"
                  className="save-name-input"
                  value={saveNameValue}
                  onChange={(e) => setSaveNameValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveNameSubmit()}
                  placeholder="Enter save name..."
                  maxLength={30}
                  autoFocus
                />
                <button className="save-name-confirm" onClick={handleSaveNameSubmit}>
                  ✓
                </button>
                <button className="save-name-cancel" onClick={() => setShowSaveNameInput(false)}>
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Slots Grid */}
          <div className="slots-section">
            <h3>Save Slots (Select One)</h3>
            <div className="slots-grid">
              {slots.map((slot) => (
                <div
                  key={slot.slot}
                  className={`slot-card ${selectedSlot === slot.slot ? 'selected' : ''} ${slot.isEmpty ? 'empty' : 'filled'} ${slot.isAutoSave ? 'auto-save' : ''}`}
                  onClick={() => handleSlotSelect(slot.slot)}
                >
                  <div className="slot-header">
                    <span className="slot-number">
                      {slot.isAutoSave ? '⚡ AUTO-SAVE' : `SLOT ${slot.slot}`}
                    </span>
                    {!slot.isEmpty && !slot.isAutoSave && (
                      <button
                        className="slot-rename-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRename(slot.slot);
                        }}
                        title="Rename"
                      >
                        ✏️
                      </button>
                    )}
                  </div>

                  {showRenameInput === slot.slot ? (
                    <div className="slot-rename-input" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleRenameSubmit(slot.slot)}
                        maxLength={30}
                        autoFocus
                      />
                      <button onClick={() => handleRenameSubmit(slot.slot)}>✓</button>
                      <button onClick={() => setShowRenameInput(null)}>✕</button>
                    </div>
                  ) : (
                    <>
                      {slot.isEmpty ? (
                        <div className="slot-empty">
                          <div className="slot-empty-icon">+</div>
                          <div className="slot-empty-text">Empty Slot</div>
                        </div>
                      ) : (
                        <div className="slot-info">
                          <div className="slot-name">{slot.slotName}</div>
                          <div className="slot-date">{slot.date}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="save-load-actions">
            <Button 
              variant="success"
              size="large"
              icon="💾"
              onClick={handleSave}
              disabled={!selectedSlot}
            >
              SAVE TO SLOT {selectedSlot || '?'}
            </Button>

            <Button 
              variant="primary"
              size="large"
              icon="📂"
              onClick={handleLoad}
              disabled={!selectedSlot || slots.find(s => s.slot === selectedSlot)?.isEmpty}
            >
              LOAD FROM SLOT {selectedSlot || '?'}
            </Button>

            <Button 
              variant="danger"
              size="large"
              icon="🗑️"
              onClick={handleDelete}
              disabled={!selectedSlot || slots.find(s => s.slot === selectedSlot)?.isEmpty}
            >
              DELETE SLOT {selectedSlot || '?'}
            </Button>
          </div>

          {/* Info Text */}
          <div className="save-load-info">
            <p>• Game auto-saves to Slot {saveLoadManager.getCurrentSlot()} every 30 seconds</p>
            <p>• Click a slot to select it, then choose an action</p>
            <p>• You can rename saves by clicking the ✏️ icon</p>
            <p>• Loading will refresh the page</p>
          </div>
    </Modal>
  );
}

export default SaveLoadModal;
